/**
 * in-game-menu.ts — the overlay shown when Esc is pressed in-game with
 * nothing else to dismiss. Per UI_UX §9.3 flow:
 *
 *   Esc → Menu
 *     ├─ Resume
 *     ├─ Save Game            (v2M2 — opens slot picker)
 *     ├─ Settings             (stubbed until v2M4)
 *     ├─ Return to Main Menu  (discard-with-confirm in v2M1)
 *     └─ Exit
 *
 * Mounted on demand by the session and torn down on Resume or on any
 * transition that leaves the in-game mode. Style mirrors main-menu.ts so
 * the two feel like the same system.
 */

import { getAudioEngine } from '../audio';

export interface InGameMenuCallbacks {
  onResume(): void;
  /** v2M2 — opens a Save slot picker. Optional so tests / sessions without saving wire can omit it. */
  onSaveGame?: () => void;
  onSettings(): void;
  onReturnToMainMenu(): void;
  /**
   * v3M12.4 — manual surrender. Triggers the `concede` command which
   * flips `gameOutcome` to `'defeat'`. Confirm gating happens at the
   * dispatch site (session.ts), matching the Return-to-Main-Menu
   * pattern.
   */
  onConcede(): void;
  onExit(): void;
}

export interface InGameMenuHandle {
  destroy(): void;
}

export function mountInGameMenu(
  parent: HTMLElement,
  callbacks: InGameMenuCallbacks,
): InGameMenuHandle {
  const overlay = document.createElement('div');
  overlay.id = 'in-game-menu-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(5, 6, 10, 0.85)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1000',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    color: '#D7DEEA',
  } as Partial<CSSStyleDeclaration>);

  const card = document.createElement('div');
  Object.assign(card.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    minWidth: '260px',
    padding: '28px 32px',
    background: '#0E131C',
    border: '1px solid #2A3344',
    borderRadius: '2px',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(card);

  const heading = document.createElement('div');
  heading.textContent = 'PAUSED';
  Object.assign(heading.style, {
    fontSize: '14px',
    letterSpacing: '0.18em',
    color: '#7E8799',
    marginBottom: '10px',
    textAlign: 'center',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(heading);

  const makeButton = (label: string, onClick: () => void): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    Object.assign(btn.style, {
      padding: '12px 20px',
      background: '#0E131C',
      color: '#D7DEEA',
      border: '1px solid #2A3344',
      borderRadius: '2px',
      fontFamily: 'inherit',
      fontSize: '14px',
      letterSpacing: '0.06em',
      cursor: 'pointer',
      textAlign: 'left',
    } as Partial<CSSStyleDeclaration>);
    btn.addEventListener('mouseenter', () => {
      btn.style.background = '#172031';
      btn.style.borderColor = '#3F4C66';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = '#0E131C';
      btn.style.borderColor = '#2A3344';
    });
    btn.addEventListener('click', onClick);
    card.appendChild(btn);
    return btn;
  };

  // Confirm prompts moved out to the callback dispatch site (main.ts +
  // session.ts) so the v2M4 Settings → "Confirm Exit" / "Confirm Return"
  // toggles can gate them. The menu now just dispatches raw intent.
  makeButton('Resume', callbacks.onResume);
  if (callbacks.onSaveGame) {
    makeButton('Save Game', callbacks.onSaveGame);
  }
  makeButton('Settings', callbacks.onSettings);
  makeButton('Concede', callbacks.onConcede);
  makeButton('Return to Main Menu', callbacks.onReturnToMainMenu);
  makeButton('Exit', callbacks.onExit);

  parent.appendChild(overlay);
  getAudioEngine().playCue('ui:panel-open');

  return {
    destroy(): void {
      getAudioEngine().playCue('ui:panel-close');
      overlay.remove();
    },
  };
}
